import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useOptionalCatalogueAppearance } from '@eshop/designs';
import { useCatalogueTemplate } from '@eshop/templates/TemplateContext';

import { useCatalogueData, useCatalogueFilters } from '../hooks';

type CatalogueDataState = ReturnType<typeof useCatalogueData>;
type CatalogueFiltersState = ReturnType<typeof useCatalogueFilters>;

export interface CatalogueDataContextValue extends CatalogueDataState {
  filters: CatalogueFiltersState;
}

interface CatalogueDataProviderProps {
  children: ReactNode;
  valueOverrides?: Partial<CatalogueDataContextValue>;
}

const CatalogueDataContext = createContext<CatalogueDataContextValue | undefined>(undefined);

export const CatalogueDataProvider = ({
  children,
  valueOverrides,
}: CatalogueDataProviderProps) => {
  const template = useCatalogueTemplate();
  const appearance = useOptionalCatalogueAppearance();

  const {
    banners,
    categorySelectedId,
    selectedCategory,
    productColumns,
    skeletonColumns,
    isItemLoading,
    hasMore,
    page,
    totalProducts,
    hasResults,
    items,
    gridConfig,
    viewMode,
    catalogueBannerSettings,
  } = useCatalogueData({ template, design: appearance?.design });

  const {
    heroEyebrow,
    categoryTitle,
    heroDescription,
    statusTags,
  } = useCatalogueFilters({
    selectedCategory,
    totalProducts,
    isItemLoading,
    hasMore,
  });

  const value = useMemo<CatalogueDataContextValue>(
    () => ({
      banners,
      categorySelectedId,
      selectedCategory,
      productColumns,
      skeletonColumns,
      isItemLoading,
      hasMore,
      page,
      totalProducts,
      hasResults,
      items,
      gridConfig,
      viewMode,
      catalogueBannerSettings,
      filters: {
        heroEyebrow,
        categoryTitle,
        heroDescription,
        statusTags,
      },
    }),
    [
      banners,
      categorySelectedId,
      selectedCategory,
      productColumns,
      skeletonColumns,
      isItemLoading,
      hasMore,
      page,
      totalProducts,
      hasResults,
      items,
      gridConfig,
      viewMode,
      catalogueBannerSettings,
      heroEyebrow,
      categoryTitle,
      heroDescription,
      statusTags,
    ],
  );

  const mergedValue = useMemo<CatalogueDataContextValue>(
    () => ({
      ...value,
      ...(valueOverrides ?? {}),
    }),
    [value, valueOverrides],
  );

  return (
    <CatalogueDataContext.Provider value={mergedValue}>
      {children}
    </CatalogueDataContext.Provider>
  );
};

export const useCatalogueDataContext = (): CatalogueDataContextValue => {
  const context = useContext(CatalogueDataContext);

  if (!context) {
    throw new Error('useCatalogueDataContext must be used within a CatalogueDataProvider');
  }

  return context;
};
